import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  fetchPlatformSettings,
  applyBrandingToDocument,
  normalizePlatformSettings,
  subscribePlatformSettings,
} from '../services/platformSettings';
import './LoginPage.css';

const ROLE_COLORS = {
  super_admin: '#C9A227',
  admin: '#3182ce',
  hr: '#38a169',
  mentor: '#805ad5',
  finance: '#d69e2e',
  employee: '#e53e3e',
};
const ROLE_LABELS = {
  super_admin: 'Founder',
  admin: 'Administrator',
  hr: 'HR Manager',
  mentor: 'Mentor',
  finance: 'Finance Dept',
  employee: 'Employee',
};

const envShowDemo = process.env.REACT_APP_SHOW_DEMO_EMAILS !== 'false';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(() => normalizePlatformSettings(null));

  const { login, authError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const apply = (next) => {
      if (cancelled) return;
      setSettings(next);
      applyBrandingToDocument(next.branding);
    };
    const unsub = subscribePlatformSettings(
      apply,
      () => {
        fetchPlatformSettings()
          .then((s) => {
            if (!cancelled) apply(s);
          })
          .catch(() => {
            if (!cancelled) {
              const fallback = normalizePlatformSettings(null);
              apply(fallback);
            }
          });
      }
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  useEffect(() => {
    const onUpdate = (e) => {
      const detail = e?.detail;
      if (!detail) return;
      setSettings((prev) =>
        normalizePlatformSettings({
          branding: detail.branding ?? prev.branding,
          notifSettings: detail.notifSettings ?? prev.notifSettings,
          geoSettings: detail.geoSettings ?? prev.geoSettings,
          emailSettings: detail.emailSettings ?? prev.emailSettings,
          loginPage: detail.loginPage ?? prev.loginPage,
        })
      );
      if (detail.branding) applyBrandingToDocument(detail.branding);
    };
    window.addEventListener('fortitude:platformSettingsUpdated', onUpdate);
    return () => window.removeEventListener('fortitude:platformSettingsUpdated', onUpdate);
  }, []);

  const { branding, loginPage } = settings;
  const showQuickFill = loginPage.showQuickFill !== false && envShowDemo;
  const demoAccounts = (loginPage.demoAccounts || []).filter((a) => a?.email);
  const emailPlaceholder =
    demoAccounts[0]?.email != null
      ? demoAccounts[0].email.replace(/^[^@]+/, 'your')
      : 'your@fitss.co.za';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    await new Promise((r) => setTimeout(r, 400));
    const result = await login(email, password);
    if (result.success) navigate('/admin');
    else setError(result.error);
    setLoading(false);
  };

  const demoPick = (acc) => {
    setEmail(acc.email);
    setPassword('');
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-grid" />
      </div>

      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <div className="login-logo-icon">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt="" className="login-logo-img" />
              ) : (
                <Zap size={28} />
              )}
            </div>
            <div>
              <h1 className="login-company">{branding.companyName || 'Fortitude IT Solutions'}</h1>
              <p className="login-tagline">{branding.tagline || 'IT Solutions'}</p>
            </div>
          </div>
          <div className="login-divider" />
          <div className="login-header">
            <Shield size={20} className="login-shield" />
            <div>
              <h2 className="login-title">{loginPage.cardTitle || 'Secure Access'}</h2>
              <p className="login-subtitle">{loginPage.cardSubtitle || 'Firebase Authentication'}</p>
            </div>
          </div>

          {authError && (
            <div className="login-error" style={{ marginBottom: 12 }}>
              <Shield size={14} /> {authError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                className="form-input"
                type="email"
                placeholder={emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group" style={{ position: 'relative' }}>
              <label className="form-label">Password</label>
              <input
                className="form-input"
                type={showPw ? 'text' : 'password'}
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ paddingRight: '44px' }}
              />
              <button type="button" className="pw-toggle" onClick={() => setShowPw(!showPw)}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {error && (
              <div className="login-error">
                <Shield size={14} /> {error}
              </div>
            )}
            <button type="submit" className="btn-gold login-btn" disabled={loading}>
              {loading ? <span className="login-spinner" /> : 'Access Dashboard'}
            </button>
          </form>

          {showQuickFill && demoAccounts.length > 0 && (
            <div className="login-demos">
              <p className="login-demo-label">{loginPage.quickFillHint}</p>
              <div className="demo-roles-grid">
                {demoAccounts.map((acc) => {
                  const label = ROLE_LABELS[acc.role] || acc.role;
                  const color = ROLE_COLORS[acc.role] || '#888';
                  return (
                    <button
                      key={acc.email}
                      className={`demo-role-btn ${email === acc.email ? 'selected' : ''}`}
                      style={{ '--rc': color }}
                      type="button"
                      onClick={() => demoPick(acc)}
                    >
                      <div className="demo-role-dot" />
                      <div className="demo-role-info">
                        <span className="demo-role-label">{label}</span>
                        <span className="demo-role-name">{acc.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="login-footer">
            <a href="/" className="login-back">
              ← Back to Website
            </a>
            <span className="login-version">{loginPage.footerBadge || 'Firebase'}</span>
          </div>
        </div>

        <div className="login-info">
          <h3 className="login-info-title">{loginPage.infoTitle || 'Role-Based Dashboards'}</h3>
          <p className="login-info-text">{loginPage.infoText}</p>
        </div>
      </div>
    </div>
  );
}
